import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, ChevronDown, ChevronLeft, Plus, Pencil, Eye, Trash2, Settings2, Lock, AlertTriangle, Copy, CopyPlus, X, Zap, Send } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { useOptions } from "@/hooks/useOptions";
import { userOptions } from "@/lib/users";
import { PageHeader } from "@/components/kit/PageHeader";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { OfferThumb } from "@/components/offers/OfferThumb";
import { BulkGenerateDialog } from "@/components/streams/BulkGenerateDialog";
import { OfferInfoCard } from "@/components/offers/OfferInfoCard";
import { OffersFilterBar, EMPTY_FILTERS, FILTER_ALL } from "@/components/offers/OffersFilterBar";
import { GeoBadges } from "@/components/users/GeoOverridesDialog";

// Max available payout of a Multi-GEO sub-offer (display only). Returns null when single-GEO
// or when every GEO pays the same — then the legacy single figure renders as before.
function maxGeoPayout(k, isBuyer) {
  if (!Array.isArray(k.geos) || k.geos.length <= 1) return null;
  let vals;
  if (isBuyer && Array.isArray(k.geo_payouts) && k.geo_payouts.length) {
    vals = k.geo_payouts.map((r) => Number(r.payout) || 0);
  } else if (k.geo_rates && Object.keys(k.geo_rates).length) {
    const base = Number(k.affiliate_payout) || 0;
    vals = [base, ...k.geos.map((g) => { const v = k.geo_rates?.[g]?.affiliate_payout; return v == null ? base : Number(v) || 0; })];
  } else return null;
  return new Set(vals).size > 1 ? Math.max(...vals) : null;
}
import { CreateOfferCardDialog } from "@/drawers/CreateOfferCardDialog";
import { EditCardDrawer } from "@/drawers/EditCardDrawer";
import { StreamDrawer } from "@/drawers/StreamDrawer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { fmtMoney, pubId, flagEmoji, geoName } from "@/lib/format";
import { toast } from "sonner";

function IconBtn({ onClick, title, danger, testId, children }) {
  return (
    <button onClick={(e) => { e.stopPropagation(); onClick(); }} title={title} data-testid={testId}
      className={`flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-white/[0.07] ${danger ? "hover:text-[#EF4444]" : "hover:text-foreground"}`}>
      {children}
    </button>
  );
}

const isSet = (v) => v && v !== FILTER_ALL;

export default function Offers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const opts = useOptions();
  const [searchParams, setSearchParams] = useSearchParams();
  // One-shot state restore when returning from the GEO offer editor (Save / Back to Offers).
  const restored = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("offers-return-state");
      if (raw) { sessionStorage.removeItem("offers-return-state"); return JSON.parse(raw); }
    } catch { /* ignore */ }
    return null;
  }, []);
  const [filters, setFilters] = useState(() => (restored?.filters ? { ...EMPTY_FILTERS, ...restored.filters } : {
    ...EMPTY_FILTERS,
    advertiser: searchParams.get("advertiser_id") || EMPTY_FILTERS.advertiser,
    product: searchParams.get("product_id") || EMPTY_FILTERS.product,
  }));
  const [expanded, setExpanded] = useState(() => new Set(restored?.expanded || []));
  const [create, setCreate] = useState(false);
  const [editCard, setEditCard] = useState({ open: false, card: null });
  const [info, setInfo] = useState({ open: false, card: null, offer: null });
  const [delGeo, setDelGeo] = useState({ open: false, geo: null });
  const [delCard, setDelCard] = useState({ open: false, card: null });
  const [selected, setSelected] = useState(() => new Set());
  const [selGeo, setSelGeo] = useState(() => new Set());
  const [geoBulkDel, setGeoBulkDel] = useState(false);
  const [geoDelInfo, setGeoDelInfo] = useState(null);
  const [pageSize, setPageSize] = useState(() => {
    const v = Number(localStorage.getItem("offers-page-size"));
    return [10, 25, 50, 100].includes(v) ? v : 25;
  });
  const [page, setPage] = useState(() => restored?.page || 1);
  const [bulkDel, setBulkDel] = useState(false);
  const [bulkDup, setBulkDup] = useState(false);
  const [streamGen, setStreamGen] = useState({ open: false, offerId: null });
  const [bulkGen, setBulkGen] = useState(false);
  const [busy, setBusy] = useState(false);

  const canEdit = hasPerm(user, "offers.edit");
  const isBuyer = user.role === "buyer";
  const canCreate = hasPerm(user, "offers.create");
  const canDelete = hasPerm(user, "offers.delete");
  const canStream = hasPerm(user, "streams.create");

  const { data, isLoading } = useQuery({ queryKey: ["offer-cards"], queryFn: async () => (await http.get("/offer-cards")).data });
  // Buyers only filter by verticals that actually occur among offers visible to THEM —
  // never the whole tenant catalog. Internal roles keep the full Settings → Verticals list.
  const visibleVerticals = useMemo(
    () => Array.from(new Set((data?.items || []).map((c) => c.vertical).filter(Boolean))).sort(),
    [data]
  );
  const filterOpts = canEdit ? opts : { ...opts, verticals: visibleVerticals };
  const refetch = () => { qc.invalidateQueries({ queryKey: ["offer-cards"] }); };

  const activeFilterCount = ["advertiser", "product", "vertical", "country", "model", "source", "status", "privacy"].filter((k) => isSet(filters[k])).length + (filters.search.trim() ? 1 : 0);
  const filtersActive = activeFilterCount > 0;

  const matchSearch = (card, k, t) => {
    if (/^\d+$/.test(t)) {
      // pure number -> match ONLY the GEO offer number, exactly (managers search by offer #)
      return String(k.tenant_seq ?? "") === t;
    }
    const idMatch = t.length >= 4; // only match uuids for longer queries
    const cardHit = [card.product_name, card.advertiser_name, card.vertical, idMatch ? card.id : ""].some((x) => String(x || "").toLowerCase().includes(t));
    if (cardHit) return true;
    return [k.name, k.geo, geoName(k.geo), k.description, `#${k.tenant_seq ?? ""}`, idMatch ? k.id : "", canEdit ? k.private_description : ""].some((x) => String(x || "").toLowerCase().includes(t));
  };

  // Relevance rank for text search: exact name match → starts with → contains → other-field hits.
  const nameRank = (name, t) => {
    const n = String(name || "").toLowerCase().trim();
    if (!n) return 3;
    if (n === t) return 0;
    if (n.startsWith(t)) return 1;
    if (n.includes(t)) return 2;
    return 3;
  };

  const cards = (data?.items || []).reduce((acc, c) => {
    if (isSet(filters.advertiser) && c.advertiser_id !== filters.advertiser) return acc;
    if (isSet(filters.product) && c.product_id !== filters.product) return acc;
    if (isSet(filters.vertical) && c.vertical !== filters.vertical) return acc;
    let kids = c.children || [];
    if (isSet(filters.country)) kids = kids.filter((k) => {
      const list = (Array.isArray(k.geos) && k.geos.length ? k.geos : String(k.geo || "").split(",").map((x) => x.trim()));
      return list.map((g) => g.toUpperCase()).includes(filters.country.toUpperCase());
    });
    if (isSet(filters.model)) kids = kids.filter((k) => k.payment_model === filters.model);
    // Strict source filter: a specific source shows ONLY sub-offers explicitly tagged with it.
    // "All Sources" on the sub-offer means no restriction on the offer itself — not a wildcard.
    if (isSet(filters.source)) kids = kids.filter((k) => k.source_id === filters.source);
    if (isSet(filters.status)) kids = kids.filter((k) => k.status === filters.status);
    if (isSet(filters.privacy)) kids = kids.filter((k) => k.privacy === filters.privacy);
    const t = filters.search.trim().toLowerCase();
    if (t) kids = kids.filter((k) => matchSearch(c, k, t));
    const childFilterActive = isSet(filters.country) || isSet(filters.model) || isSet(filters.source) || isSet(filters.status) || isSet(filters.privacy) || !!t;
    if (childFilterActive) {
      if (kids.length === 0) return acc;
      acc.push({ ...c, children: kids, _rank: t ? Math.min(nameRank(c.product_name, t), ...kids.map((k) => nameRank(k.name, t))) : 0 });
    } else {
      acc.push(c);
    }
    return acc;
  }, []);
  // Strong Offer/Product name matches surface first; ties keep the original newest-first order (stable sort).
  if (filters.search.trim()) cards.sort((a, b) => (a._rank ?? 3) - (b._rank ?? 3));

  // Top-level offer cards pagination (same UX as the platform's tables).
  const pages = Math.max(1, Math.ceil(cards.length / pageSize));
  const curPage = Math.min(page, pages);
  const pagedCards = cards.slice((curPage - 1) * pageSize, curPage * pageSize);
  const firstFilterRun = React.useRef(true);
  React.useEffect(() => {
    if (firstFilterRun.current) { firstFilterRun.current = false; return; }
    setPage(1);
  }, [filters]);

  // Preserve the exact list state (search/filters, expanded product, page) while editing a GEO offer.
  const gotoEditor = (cardId, geoId) => {
    try {
      sessionStorage.setItem("offers-return-state", JSON.stringify({ filters, expanded: [...expanded], page: curPage }));
    } catch { /* ignore */ }
    navigate(`/offers/${cardId}/geo/${geoId}`);
  };

  // Options for mass stream generation (active GEO offers × users).
  // Same ordering as everywhere else: by public ID (#1, #2, #3…), never grouped by product.
  const genOfferOptions = useMemo(() => (data?.items || []).flatMap((c) => (c.children || [])
    .filter((k) => k.status === "active")
    .map((k) => ({
      value: k.id,
      seq: Number(k.tenant_seq ?? 0),
      label: `#${k.tenant_seq ?? ""} ${c.product_name || "Offer"} — ${String(k.geo || "").toUpperCase()}`.trim(),
      search: `#${k.tenant_seq ?? ""} ${k.tenant_seq ?? ""} ${k.name || ""} ${c.product_name || ""} ${String(k.geo || "").toUpperCase()}`,
    })))
    .sort((a, b) => b.seq - a.seq), [data]);
  const genBuyerOptions = useMemo(() => userOptions(opts.users), [opts]);

  const toggle = (id) => setExpanded((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const isOpenCard = (id) => filtersActive || expanded.has(id);

  const addGeo = async (card) => {
    setBusy(true);
    try {
      const { data: o } = await http.post("/offers", {
        parent_id: card.id, advertiser_id: card.advertiser_id, product_id: card.product_id,
        vertical: card.vertical, name: card.product_name,
        payment_model: card.payment_model, currency: card.currency, privacy: card.privacy, status: "active",
      });
      refetch();
      gotoEditor(card.id, o.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add GEO"); }
    finally { setBusy(false); }
  };

  const doDeleteGeo = async () => {
    setBusy(true);
    try { await http.delete(`/offers/${delGeo.geo.id}`); refetch(); toast.success("GEO offer deleted"); setDelGeo({ open: false, geo: null }); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };
  const doDeleteCard = async () => {
    setBusy(true);
    try { await http.delete(`/offer-cards/${delCard.card.id}`); refetch(); toast.success("Offer deleted"); setDelCard({ open: false, card: null }); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  const cloneGeo = async (card, geo) => {
    setBusy(true);
    try {
      const { data: o } = await http.post(`/offers/${geo.id}/clone`);
      refetch();
      toast.success("GEO offer cloned");
      gotoEditor(card.id, o.id);
    } catch (e) { toast.error(e.response?.data?.detail || "Clone failed"); }
    finally { setBusy(false); }
  };

  // ----- bulk card selection (mutually exclusive with sub-offer selection) -----
  const toggleSelect = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectedIds = () => [...selected];
  const clearSelection = () => setSelected(new Set());

  // ----- bulk SUB-OFFER selection -----
  const toggleGeoSelect = (id) => setSelGeo((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearGeoSelection = () => setSelGeo(new Set());

  const doGeoBulkSet = async (patch, label) => {
    setBusy(true);
    try {
      const { data: res } = await http.post("/offers/bulk-edit", { ids: [...selGeo], set: patch });
      refetch();
      toast.success(`${label} — ${res.changed} sub-offer${res.changed === 1 ? "" : "s"} updated`);
      clearGeoSelection();
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk edit failed"); }
    finally { setBusy(false); }
  };

  const openGeoBulkDelete = async () => {
    setGeoDelInfo(null); setGeoBulkDel(true);
    try {
      const { data: res } = await http.post("/offers/bulk-delete", { ids: [...selGeo], check: true });
      setGeoDelInfo(res);
    } catch { /* preview is best-effort */ }
  };
  const doGeoBulkDelete = async () => {
    setBusy(true);
    try {
      const { data: res } = await http.post("/offers/bulk-delete", { ids: [...selGeo] });
      refetch(); clearGeoSelection(); setGeoBulkDel(false);
      toast.success(`Deleted ${res.deleted} sub-offer${res.deleted === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed"); }
    finally { setBusy(false); }
  };

  const doBulkDelete = async () => {
    setBusy(true);
    const ids = selectedIds();
    try {
      await Promise.all(ids.map((id) => http.delete(`/offer-cards/${id}`)));
      refetch(); clearSelection(); setBulkDel(false);
      toast.success(`Deleted ${ids.length} offer${ids.length === 1 ? "" : "s"}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk delete failed"); }
    finally { setBusy(false); }
  };
  const doBulkDuplicate = async (includeOffers) => {
    setBusy(true);
    const ids = selectedIds();
    try {
      await Promise.all(ids.map((id) => http.post(`/offer-cards/${id}/duplicate`, { include_offers: includeOffers })));
      refetch(); clearSelection(); setBulkDup(false);
      toast.success(`Duplicated ${ids.length} offer${ids.length === 1 ? "" : "s"}${includeOffers ? " with GEOs" : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk duplicate failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader title="Offers" subtitle="Each product groups its GEO offers. Expand a product to see its GEOs." />

      <OffersFilterBar internal={canEdit} opts={filterOpts} filters={filters} setFilters={setFilters}
        onReset={() => { setFilters(EMPTY_FILTERS); setSearchParams({}); }} activeCount={activeFilterCount}
        rightSlot={(
          <>
            {canStream && (
              <Button onClick={() => (isBuyer ? setStreamGen({ open: true, offerId: null }) : setBulkGen(true))} variant="outline" data-testid="add-stream-top-btn" className="h-10 gap-1.5 border-white/[0.12]">
                <Zap className="h-4 w-4" /> Generate Stream
              </Button>
            )}
            {canCreate && (
              <Button onClick={() => setCreate(true)} data-testid="add-offer-btn" className="h-10 gap-1.5 bg-white text-black hover:bg-white/90">
                <Plus className="h-4 w-4" /> Add Offer
              </Button>
            )}
          </>
        )} />

      {selected.size > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-white/[0.1] bg-[#141417] px-4 py-2.5" data-testid="offers-bulk-bar">
          <span className="text-sm font-medium text-foreground">{selected.size} selected</span>
          <div className="ml-auto flex items-center gap-2">
            {canCreate && <Button variant="outline" onClick={() => setBulkDup(true)} data-testid="bulk-duplicate-btn" className="h-9 gap-1.5 border-white/[0.12]"><CopyPlus className="h-4 w-4" /> Duplicate</Button>}
            {canDelete && <Button variant="outline" onClick={() => setBulkDel(true)} data-testid="bulk-delete-btn" className="h-9 gap-1.5 border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 className="h-4 w-4" /> Delete</Button>}
            <Button variant="ghost" onClick={clearSelection} data-testid="bulk-clear-btn" className="h-9 gap-1.5 text-muted-foreground"><X className="h-4 w-4" /> Clear</Button>
          </div>
        </div>
      )}

      {selGeo.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[#38BDF8]/25 bg-[#141417] px-4 py-2.5" data-testid="suboffers-bulk-bar">
          <span className="text-sm font-medium text-foreground">{selGeo.size} sub-offer{selGeo.size === 1 ? "" : "s"} selected</span>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={busy} data-testid="geo-bulk-status-btn" className="h-9 gap-1.5 border-white/[0.12]">Status ▾</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-white/[0.1] bg-[#1a1a1e]">
                <DropdownMenuItem data-testid="geo-bulk-set-active" onClick={() => doGeoBulkSet({ status: "active" }, "Status → Active")} className="cursor-pointer text-sm focus:bg-white/[0.06]">Set Active</DropdownMenuItem>
                <DropdownMenuItem data-testid="geo-bulk-set-inactive" onClick={() => doGeoBulkSet({ status: "disabled" }, "Status → Disabled")} className="cursor-pointer text-sm focus:bg-white/[0.06]">Set Disabled</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={busy} data-testid="geo-bulk-privacy-btn" className="h-9 gap-1.5 border-white/[0.12]">Privacy ▾</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="border-white/[0.1] bg-[#1a1a1e]">
                <DropdownMenuItem data-testid="geo-bulk-privacy-public" onClick={() => doGeoBulkSet({ privacy: "public" }, "Privacy → Public")} className="cursor-pointer text-sm focus:bg-white/[0.06]">Public</DropdownMenuItem>
                <DropdownMenuItem data-testid="geo-bulk-privacy-request" onClick={() => doGeoBulkSet({ privacy: "request" }, "Privacy → Request")} className="cursor-pointer text-sm focus:bg-white/[0.06]">Request</DropdownMenuItem>
                <DropdownMenuItem data-testid="geo-bulk-privacy-private" onClick={() => doGeoBulkSet({ privacy: "private" }, "Privacy → Private")} className="cursor-pointer text-sm focus:bg-white/[0.06]">Hidden / Private</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={busy} data-testid="geo-bulk-source-btn" className="h-9 gap-1.5 border-white/[0.12]">Change Source ▾</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="max-h-72 overflow-y-auto border-white/[0.1] bg-[#1a1a1e]">
                <DropdownMenuItem data-testid="geo-bulk-source-all" onClick={() => doGeoBulkSet({ source_id: null }, "Source → All Sources")} className="cursor-pointer text-sm focus:bg-white/[0.06]">All Sources</DropdownMenuItem>
                {(opts.sources || []).map((s) => (
                  <DropdownMenuItem key={s.id} data-testid={`geo-bulk-source-${s.id}`} onClick={() => doGeoBulkSet({ source_id: s.id }, `Source → ${s.name}`)} className="cursor-pointer text-sm focus:bg-white/[0.06]">{s.name}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {canDelete && <Button variant="outline" disabled={busy} onClick={openGeoBulkDelete} data-testid="geo-bulk-delete-btn" className="h-9 gap-1.5 border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10"><Trash2 className="h-4 w-4" /> Delete</Button>}
            <Button variant="ghost" onClick={clearGeoSelection} data-testid="geo-bulk-clear-btn" className="h-9 gap-1.5 text-muted-foreground"><X className="h-4 w-4" /> Clear</Button>
          </div>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-white/[0.08] bg-[#141417]" data-testid="offers-tree">
        {isLoading && Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="border-b border-white/[0.05] px-4 py-3.5"><Skeleton className="h-6 w-1/2 bg-white/[0.06]" /></div>
        ))}
        {!isLoading && cards.length === 0 && (
          <div className="py-16 text-center text-sm text-muted-foreground">{filtersActive ? "No offers match your filters." : (canCreate ? "No offers — click Add Offer to create one." : "No offers available.")}</div>
        )}

        {!isLoading && pagedCards.map((c) => {
          const isOpen = isOpenCard(c.id);
          return (
            <div key={c.id}>
              {/* parent row — click toggles GEO only */}
              <div data-testid={`offer-card-${c.id}`} onClick={() => toggle(c.id)}
                className="flex cursor-pointer items-center gap-3.5 border-b border-white/[0.05] px-4 py-4 transition-colors hover:bg-white/[0.025]">
                {canEdit && (
                  <span onClick={(e) => e.stopPropagation()} className="flex shrink-0 items-center">
                    <Checkbox checked={selected.has(c.id)} disabled={selGeo.size > 0} onCheckedChange={() => toggleSelect(c.id)} data-testid={`card-select-${c.id}`}
                      className={selGeo.size > 0 ? "opacity-30" : ""} />
                  </span>
                )}
                <span data-testid={`offer-card-toggle-${c.id}`} className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-muted-foreground">
                  {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </span>
                <OfferThumb image={c.product_image} name={c.product_name} size="lg" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-base font-semibold tracking-tight text-foreground">{c.product_name}</div>
                  <div className="mt-1 flex min-w-0 items-center gap-2 text-xs text-muted-foreground">
                    {!isBuyer && c.advertiser_name && <span className="truncate">{c.advertiser_name}</span>}
                    {c.vertical && <span className="shrink-0 rounded border border-white/[0.1] bg-white/[0.03] px-1.5 py-0.5 text-[11px]">{c.vertical}</span>}
                  </div>
                </div>
                <span className="hidden text-sm text-muted-foreground sm:inline">{c.payment_model}</span>
                <span className="inline-flex items-center rounded-full bg-white/[0.06] px-2 py-0.5 font-mono text-xs text-foreground">{c.geo_count} GEO</span>
                {(canCreate || canEdit || canDelete) && (
                  <div className="flex items-center gap-1">
                    {canCreate && <IconBtn onClick={() => addGeo(c)} title="Add GEO" testId={`card-add-geo-${c.id}`}><Plus className="h-4 w-4" /></IconBtn>}
                    {canEdit && <IconBtn onClick={() => setEditCard({ open: true, card: c })} title="Edit card" testId={`card-edit-${c.id}`}><Settings2 className="h-4 w-4" /></IconBtn>}
                    {canDelete && <IconBtn onClick={() => setDelCard({ open: true, card: c })} title="Delete offer" danger testId={`card-delete-${c.id}`}><Trash2 className="h-4 w-4" /></IconBtn>}
                  </div>
                )}
              </div>

              {/* children GEO rows — actions live here */}
              {isOpen && (
                <div className="bg-[#0E0E11]" data-testid={`offer-card-children-${c.id}`}>
                  {(c.children || []).length === 0 && (
                    <div className="px-4 py-3 pl-16 text-sm text-muted-foreground">
                      No GEO offers yet.{canCreate && <button onClick={() => addGeo(c)} className="ml-1 text-[#60A5FA] hover:underline">Add the first GEO</button>}
                    </div>
                  )}
                  {(c.children || []).map((k) => (
                    <div key={k.id} data-testid={`offer-geo-${k.id}`} onClick={() => setInfo({ open: true, card: c, offer: k })}
                      className="flex cursor-pointer flex-wrap items-center gap-3 border-b border-white/[0.04] py-2.5 pl-16 pr-4 transition-colors hover:bg-white/[0.03]">
                      {canEdit && (
                        <span onClick={(e) => e.stopPropagation()} className="flex shrink-0 items-center">
                          <Checkbox checked={selGeo.has(k.id)} disabled={selected.size > 0} onCheckedChange={() => toggleGeoSelect(k.id)} data-testid={`geo-select-${k.id}`}
                            className={selected.size > 0 ? "opacity-30" : ""} />
                        </span>
                      )}
                      <span className="font-mono text-xs text-muted-foreground">{pubId(k)}</span>
                      <span className="flex items-center gap-2 text-sm font-medium text-foreground">
                        <GeoBadges geo={k.geo} geos={k.geos} testId={`geo-badges-${k.id}`} />
                      </span>
                      <span className="rounded bg-white/[0.06] px-1.5 py-0.5 text-xs text-muted-foreground">{k.payment_model}</span>
                      {k.source_name ? (
                        <span data-testid={`geo-source-${k.id}`} className="rounded border border-white/[0.1] bg-white/[0.04] px-1.5 py-0.5 text-xs text-foreground/80">{k.source_name}</span>
                      ) : (
                        <span data-testid={`geo-source-${k.id}`} className="rounded border border-white/[0.06] px-1.5 py-0.5 text-xs text-muted-foreground/60">All Sources</span>
                      )}
                      {(() => { const upTo = maxGeoPayout(k, isBuyer); return upTo != null ? (
                        <span className="font-mono text-sm font-semibold text-[#22C55E]" data-testid={`geo-payout-${k.id}`} title="Maximum payout across this offer's GEOs — see the offer card for the full breakdown">
                          <span className="mr-1 font-sans text-[10px] font-normal text-muted-foreground">up to</span>
                          {k.payment_model === "RevShare" ? `${upTo}%` : fmtMoney(upTo, k.currency)}
                        </span>
                      ) : isBuyer && k.payout_is_personal ? (
                        <span className="flex items-center gap-1.5 font-mono text-sm" data-testid={`geo-payout-${k.id}`}>
                          <span className="text-muted-foreground/50 line-through">{k.payment_model === "RevShare" ? `${Number(k.payout_default) || 0}%` : fmtMoney(k.payout_default)}</span>
                          <span className="font-semibold text-[#22C55E]">{k.payment_model === "RevShare" ? `${Number(k.buyer_payout) || 0}%` : fmtMoney(k.buyer_payout)}</span>
                          {k.payment_model === "Hybrid" && <span className="font-semibold text-[#F59E0B]">· {Number(k.buyer_rs_percent ?? k.rs_percent_default) || 0}%</span>}
                        </span>
                      ) : (
                        <span className="font-mono text-sm font-semibold text-[#22C55E]" data-testid={`geo-payout-${k.id}`}>
                          {k.payment_model === "RevShare"
                            ? `${Number(isBuyer ? (k.buyer_payout ?? k.affiliate_payout) : k.affiliate_payout) || 0}%`
                            : fmtMoney(isBuyer ? (k.buyer_payout ?? k.affiliate_payout) : k.affiliate_payout, k.currency)}
                          {k.payment_model === "Hybrid" && (
                            <span className="text-[#F59E0B]"> · {Number(isBuyer ? (k.buyer_rs_percent ?? k.rs_percent_default) : k.rs_affiliate_percent) || 0}%</span>
                          )}
                        </span>
                      ); })()}
                      {canEdit ? (
                        k.privacy === "public" ? <StatusBadge status="public" />
                          : k.privacy === "request" ? <StatusBadge status="request" />
                          : <span className="inline-flex items-center gap-1 text-xs text-muted-foreground/70" title="Private offer"><Lock className="h-3 w-3" /> Private</span>
                      ) : (
                        <>
                          {k.access_state === "pending" && <StatusBadge status="pending" />}
                          {k.access_state === "rejected" && <StatusBadge status="rejected" />}
                          {k.access_state === "request_required" && (
                            <span className="inline-flex items-center gap-1 text-xs text-[#38BDF8]"><Send className="h-3 w-3" /> Request access</span>
                          )}
                        </>
                      )}
                      <StatusBadge status={k.status} />
                      {canEdit && k.private_description && (
                        <span data-testid={`geo-note-${k.id}`} title={k.private_description}
                          className="inline-flex max-w-[220px] items-center gap-1 truncate rounded border border-[#EAB308]/30 bg-[#EAB308]/[0.12] px-1.5 py-0.5 text-xs text-[#EAB308]">
                          <AlertTriangle className="h-3 w-3 shrink-0" /> <span className="truncate">{k.private_description}</span>
                        </span>
                      )}
                      <div className="ml-auto flex items-center gap-1">
                        <IconBtn onClick={() => setInfo({ open: true, card: c, offer: k })} title="View" testId={`geo-preview-${k.id}`}><Eye className="h-4 w-4" /></IconBtn>
                        {canEdit && (
                          <>
                            <IconBtn onClick={() => gotoEditor(c.id, k.id)} title="Edit" testId={`geo-edit-${k.id}`}><Pencil className="h-4 w-4" /></IconBtn>
                            {canStream && k.status === "active" && <IconBtn onClick={() => setStreamGen({ open: true, offerId: k.id })} title="Generate Stream" testId={`geo-genstream-${k.id}`}><Zap className="h-4 w-4" /></IconBtn>}
                            {canCreate && <IconBtn onClick={() => cloneGeo(c, k)} title="Clone (same card)" testId={`geo-clone-${k.id}`}><Copy className="h-4 w-4" /></IconBtn>}
                            {canDelete && <IconBtn onClick={() => setDelGeo({ open: true, geo: k })} title="Delete" danger testId={`geo-delete-${k.id}`}><Trash2 className="h-4 w-4" /></IconBtn>}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!isLoading && cards.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground" data-testid="offers-pagination">
          <div className="flex items-center gap-2">
            <span>Offers per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); try { localStorage.setItem("offers-page-size", v); } catch { /* ignore */ } }}>
              <SelectTrigger className="h-8 w-[80px] border-white/[0.1] bg-[#0A0A0A] text-xs" data-testid="offers-page-size"><SelectValue /></SelectTrigger>
              <SelectContent className="border-white/[0.1] bg-[#1a1a1e]">
                {[10, 25, 50, 100].map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <span data-testid="offers-total">{cards.length} offer{cards.length === 1 ? "" : "s"}</span>
          </div>
          <div className="flex items-center gap-2">
            <button disabled={curPage <= 1} onClick={() => setPage(curPage - 1)} data-testid="offers-prev"
              className="rounded-md border border-white/[0.1] p-1.5 disabled:opacity-30 hover:bg-white/[0.05]"><ChevronLeft className="h-3.5 w-3.5" /></button>
            <span data-testid="offers-page-indicator">Page {curPage} / {pages}</span>
            <button disabled={curPage >= pages} onClick={() => setPage(curPage + 1)} data-testid="offers-next"
              className="rounded-md border border-white/[0.1] p-1.5 disabled:opacity-30 hover:bg-white/[0.05]"><ChevronRight className="h-3.5 w-3.5" /></button>
          </div>
        </div>
      )}

      <CreateOfferCardDialog open={create} onOpenChange={setCreate} />
      <BulkGenerateDialog open={bulkGen} onOpenChange={setBulkGen} offerOptions={genOfferOptions} buyerOptions={genBuyerOptions} />
      <EditCardDrawer open={editCard.open} card={editCard.card} onOpenChange={(o) => setEditCard((s) => ({ ...s, open: o }))} />
      <OfferInfoCard open={info.open} card={info.card} offer={info.offer} canCreateStream={canStream}
        onOpenChange={(o) => setInfo((s) => ({ ...s, open: o }))} />
      <StreamDrawer open={streamGen.open} item={streamGen.offerId ? { offer_id: streamGen.offerId } : null}
        onOpenChange={(o) => setStreamGen((s) => ({ ...s, open: o }))} />

      <AlertDialog open={delGeo.open} onOpenChange={(o) => setDelGeo((s) => ({ ...s, open: o }))}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="geo-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete GEO Offer?</AlertDialogTitle>
            <AlertDialogDescription>
              This action is permanent and cannot be undone. Deleting <span className="font-medium text-foreground">{delGeo.geo?.name} ({geoName(delGeo.geo?.geo)})</span> will permanently remove all related data, including:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="ml-1 space-y-1 rounded-lg border border-[#EF4444]/25 bg-[#EF4444]/[0.06] p-3 text-xs text-muted-foreground">
            <li>• all Streams linked to this GEO</li>
            <li>• all Clicks, Leads and Sales (Hold, Approved and Trash)</li>
            <li>• all Postbacks and delivery logs</li>
            <li>• all Statistics and financial records</li>
            <li>• all Buyer payouts and balances tied to these conversions</li>
          </ul>
          <p className="text-xs text-muted-foreground">All reports, statistics and wallet balances will be recalculated automatically. <span className="font-medium text-foreground">This action cannot be reversed.</span></p>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="geo-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteGeo} disabled={busy} data-testid="geo-delete-confirm" className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={delCard.open} onOpenChange={(o) => setDelCard((s) => ({ ...s, open: o }))}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="card-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Offer?</AlertDialogTitle>
            <AlertDialogDescription>
              This action is permanent and cannot be undone. Deleting <span className="font-medium text-foreground">{delCard.card?.product_name}</span> and all its {delCard.card?.geo_count} GEO offers will permanently remove all related Streams, Clicks, Leads, Sales (Hold, Approved and Trash), Postbacks, Statistics, financial records and Buyer payouts/balances tied to these conversions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <p className="text-xs text-muted-foreground">All reports, statistics and wallet balances will be recalculated automatically. <span className="font-medium text-foreground">This action cannot be reversed.</span></p>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="card-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDeleteCard} disabled={busy} data-testid="card-delete-confirm" className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Delete all permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={geoBulkDel} onOpenChange={setGeoBulkDel}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="geo-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selGeo.size} sub-offer{selGeo.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action is permanent and cannot be undone. The selected GEO sub-offers will be permanently removed with all related data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {geoDelInfo && (geoDelInfo.streams > 0 || geoDelInfo.conversions > 0 || geoDelInfo.clicks > 0) && (
            <div className="rounded-lg border border-[#EF4444]/25 bg-[#EF4444]/[0.06] p-3 text-xs text-muted-foreground" data-testid="geo-bulk-delete-impact">
              <p className="mb-1.5 font-medium text-[#EF4444]">This deletion also removes:</p>
              <ul className="space-y-1">
                {geoDelInfo.streams > 0 && <li>• {geoDelInfo.streams} linked Stream{geoDelInfo.streams === 1 ? "" : "s"}</li>}
                {geoDelInfo.clicks > 0 && <li>• {geoDelInfo.clicks} Click{geoDelInfo.clicks === 1 ? "" : "s"}</li>}
                {geoDelInfo.conversions > 0 && <li>• {geoDelInfo.conversions} Conversion{geoDelInfo.conversions === 1 ? "" : "s"} (Hold, Approved and Trash) incl. buyer payouts/balances</li>}
                <li>• all Postbacks, delivery logs, Statistics and financial records tied to them</li>
              </ul>
            </div>
          )}
          <p className="text-xs text-muted-foreground">All reports, statistics and wallet balances will be recalculated automatically. <span className="font-medium text-foreground">This action cannot be reversed.</span></p>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="geo-bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doGeoBulkDelete} disabled={busy} data-testid="geo-bulk-delete-confirm" className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDel} onOpenChange={setBulkDel}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selected.size} offer{selected.size === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>The selected offer cards and all their GEO offers (and access grants) will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doBulkDelete} disabled={busy} data-testid="bulk-delete-confirm" className="bg-[#EF4444] text-white hover:bg-[#dc2626]">Delete selected</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDup} onOpenChange={setBulkDup}>
        <AlertDialogContent className="border-white/[0.1] bg-[#141417]" data-testid="bulk-duplicate-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate {selected.size} offer{selected.size === 1 ? "" : "s"}</AlertDialogTitle>
            <AlertDialogDescription>Choose what to copy. Advertiser, Product and Vertical are always preserved on the copy.</AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid gap-2 py-1">
            <button onClick={() => doBulkDuplicate(false)} disabled={busy} data-testid="bulk-duplicate-card-only"
              className="flex items-center gap-3 rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-4 py-3 text-left transition-colors hover:border-white/30 disabled:opacity-50">
              <Copy className="h-4 w-4 text-muted-foreground" />
              <div><div className="text-sm font-medium text-foreground">Duplicate card only</div><div className="text-xs text-muted-foreground">Empty copy — no GEO offers.</div></div>
            </button>
            <button onClick={() => doBulkDuplicate(true)} disabled={busy} data-testid="bulk-duplicate-with-geos"
              className="flex items-center gap-3 rounded-lg border border-white/[0.1] bg-[#0A0A0A] px-4 py-3 text-left transition-colors hover:border-white/30 disabled:opacity-50">
              <CopyPlus className="h-4 w-4 text-muted-foreground" />
              <div><div className="text-sm font-medium text-foreground">Duplicate card with all GEO offers</div><div className="text-xs text-muted-foreground">Copies the card and clones every GEO offer inside it.</div></div>
            </button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-duplicate-cancel">Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
